import React, { useState } from 'react'; // useState is needed for the currency slider
import LandingNav from '../components/Landing-Nav.js';
import FooterLanding from '../components/FooterLanding.js';
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

export default function PricingComponent() {
    const navigate = useNavigate();
    return (<>
    <LandingNav/>
    <section className="bg-[#0A1635] min-h-screen text-white">
  <div className="max-w-screen-xl mx-auto px-6 py-12 md:py-20 flex flex-col md:flex-row items-center md:items-start gap-12">
    
    {/* Left Side - Text Content */}
    <div className="flex-1 text-left">
      <p className="text-blue-400 font-medium mb-2 text-sm md:text-base">
        Shiftry | Employee Scheduling Software
      </p>
      <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-4">
        Perfect staff schedules, every time
      </h1>
      <p className="text-gray-300 text-base md:text-lg mb-6">
        Managing shift patterns for service professionals can be challenging. 
        Shiftry simplifies workforce scheduling by combining intelligent automation, 
        real-time communication, and an intuitive interface—so your team stays organized 
        and your operations run smoothly.
      </p>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-3 sm:space-y-0">
        <button className="bg-blue-600 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700 transition" onClick={() => navigate("/contact")}>
          Request demo
        </button>
        {/* <button className="text-white border border-white px-6 py-3 rounded-md font-medium hover:bg-gray-200 hover:text-[#0A1635] transition">
          Speak to the team
        </button> */}
      </div>
    </div>

    {/* Right Side - Image */}
    <div className="flex-1 flex justify-center">
      <img 
        src="/schedule3d.png" 
        alt="Scheduling platform demo" 
        className="w-full max-w-sm md:max-w-lg rounded-lg shadow-xl"
      />
    </div>
  </div>
</section>

<section className="bg-white min-h-screen py-16 flex items-center">
  <div className="max-w-screen-xl mx-auto px-6 flex flex-col md:flex-row items-center gap-12">
     <div className="flex-1 flex justify-center">
      <img 
        src="/addschedule.png" 
        alt="Scheduling illustration" 
        className="w-full max-w-md md:max-w-lg rounded-lg shadow-2xl"
      />
    </div>
    {/* Left Side - Text Content */}
    <div className="flex-1 text-left">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Build Schedules Effortlessly
      </h2>

      <p className="text-gray-700 text-lg mb-6">
        With Shiftry, managers can put together work shifts in a fraction of 
        the time it normally takes—without the stress of spreadsheets or guesswork.
      </p>

      <ul className="space-y-4">
        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600">
              <Check size={16} />
            </span>
          </span>
          <span className="ml-3 text-gray-700">
            <strong>Quickly create or fine-tune rosters</strong> in just a few steps
          </span>
        </li>

        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600">
              <Check size={16} />
            </span>
          </span>
          <span className="ml-3 text-gray-700">
            <strong>Save and reuse proven shift layouts</strong> for repeating schedules
          </span>
        </li>

        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600">
              <Check size={16} />
            </span>
          </span>
          <span className="ml-3 text-gray-700">
            <strong>Automatic checks</strong> against staff availability, skills, 
            and Employee rules keep plans accurate and compliant
          </span>
        </li>
      </ul>
    </div>

    {/* Right Side - Image */}
   
  </div>
</section>


      <section className="bg-gray-50 min-h-screen py-16">
  <div className="max-w-screen-xl mx-auto px-6 flex flex-col md:flex-row items-center gap-12">
    
    {/* Left Side - Text */}
    <div className="flex-1 text-left">
      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        A Simple, Powerful Scheduling Experience
      </h2>
      <p className="text-gray-700 text-lg mb-6">
        Our scheduling editor is designed for ease and performance—whether you’re 
        overseeing one department or multiple locations.
      </p>

      <ul className="space-y-4">
        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1 w-6 h-6 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full mr-3">
            ✓
          </span>
          <span className="text-gray-700">
            Drag-and-drop shifts to assign staff effortlessly
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1 w-6 h-6 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full mr-3">
            ✓
          </span>
          <span className="text-gray-700">
            View rosters by <em>day, week, or month</em>
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1 w-6 h-6 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full mr-3">
            ✓
          </span>
          <span className="text-gray-700">
            Switch seamlessly between <em>teams, departments, or sites</em>
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1 w-6 h-6 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full mr-3">
            ✓
          </span>
          <span className="text-gray-700">
            Track <em>coverage, workload, and performance metrics</em> in real time
          </span>
        </li>
        <li className="flex items-start">
          <span className="flex-shrink-0 mt-1 w-6 h-6 bg-blue-100 text-blue-600 flex items-center justify-center rounded-full mr-3">
            ✓
          </span>
          <span className="text-gray-700">
            Fully optimized for <em>desktop, tablet, and mobile</em>
          </span>
        </li>
      </ul>
    </div>

    {/* Right Side - Image */}
    <div className="flex-1 flex justify-center">
      <img 
        src="/schedulepage.png" 
        alt="Scheduling editor preview" 
        className="w-full max-w-md md:max-w-lg rounded-lg shadow-lg"
      />
    </div>
  </div>
</section>



    <FooterLanding/>
    </>)
}
